"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { GraduationCap, Headphones, ShieldCheck, ArrowRight, Award, BookOpen, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
const highlights = [{
  icon: GraduationCap,
  title: "Application-Led Training",
  description: "Hands-on operator and method development training tailored to your exact instrumentation."
}, {
  icon: Headphones,
  title: "Direct Specialist Support",
  description: "Fast technical escalation with senior application chemists and service engineers."
}, {
  icon: ShieldCheck,
  title: "GMP & Regulated Compliance",
  description: "Complete qualification documentation, protocols, and audit-ready operator certification."
}];
const stats = [{
  label: "Audit Preparation Support"
}, {
  label: "Priority Response Available"
}, {
  label: "Analysts Trained"
}];
const avatars = [{
  name: "Dr. Sarah Kelly",
  role: "Senior Chemist",
  initials: "SK"
}, {
  name: "Liam O'Connor",
  role: "Lead Field Engineer",
  initials: "LO"
}, {
  name: "Elena Walsh",
  role: "Compliance Specialist",
  initials: "EW"
}];
export default function TrainingSupport() {
  return <section id="trainingsupporthero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative background grid pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]">
        <svg className="w-full h-full" width="100%" height="100%">
          <defs>
            <pattern id="hero-grid-pattern" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hero-grid-pattern)" />
        </svg>
      </div>

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Headline and Call-to-Actions */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-6 text-left">
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="flex items-center">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                <li className="inline-flex items-center">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5 text-muted-foreground/60" />
                </li>
                <li className="inline-flex items-center">
                  <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                    Services
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5 text-muted-foreground/60" />
                </li>
                <li className="inline-flex items-center">
                  <span className="font-medium text-foreground" aria-current="page">
                    Training &amp; Application Support
                  </span>
                </li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase border border-border">
                <BookOpen className="mr-1.5 size-3.5 text-primary" />
                Specialist Lifecycle Services
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs">
                Ireland &amp; UK Regulated Labs
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Comprehensive Training &amp; Fast-Response Technical Support
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl font-normal leading-relaxed">
              Empower your laboratory team with certified instrumentation training, method transfer assistance, and qualification support available designed for regulated pharmaceutical, chemical, and biopharma environments.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <Button asChild size="lg" className="font-semibold shadow-sm transition-colors">
                <Link to="/ContactPage">
                  Request Support Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-semibold transition-colors">
                <Link to="/ServicesPage">
                  View All Services
                </Link>
              </Button>
            </div>

            {/* Trust and Social Proof Block */}
            <div className="pt-6 border-t border-border/80 flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex -space-x-2 overflow-hidden">
                {avatars.map((avatar, i) => <div key={avatar.name} data-index={i} className="inline-block ring-2 ring-background rounded-full">
                    <SafeHydrate><Avatar className="size-9 bg-muted border border-border">
                      <AvatarImage src={`/img/avatar_specialist_${i + 1}.webp`} alt={avatar.name} />
                      <AvatarFallback className="text-xs font-semibold text-foreground">
                        {avatar.initials}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                  </div>)}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                <span className="font-semibold text-foreground block sm:inline">Certified Technical Engineers: </span>
                Delivering scheduled on-site modules and Remote diagnostics support.
              </div>
            </div>

          </div>

          {/* Right Column: Key Service Pillars & Interactive Summary Card */}
          <div className="lg:col-span-5 flex flex-col space-y-4">
            
            {/* Main Visual Feature Card */}
            <Card className="border border-border/80 shadow-md bg-card overflow-hidden">
              <div className="relative h-48 sm:h-56 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_training_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_training_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_training_session.png-wvc-sizes" alt="Laboratory technicians undergoing analytical instrument training" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                <div className="absolute top-4 left-4">
                  <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs">
                    <Award className="mr-1 size-3.5" />
                    Certified Modules
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-2 border-b border-border pb-5 text-center">
                  {stats.map((stat, i) => <div key={stat.label} data-index={i} className="flex flex-col items-center justify-center p-1">
                      <span className="text-xs sm:text-sm font-semibold text-foreground leading-snug">
                        {stat.label}
                      </span>
                    </div>)}
                </div>

                <div className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Included in Support Agreements
                  </div>
                  <ul className="space-y-2 text-xs sm:text-sm text-foreground">
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>Customised on-site &amp; virtual operator training sessions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>Preventative maintenance &amp; rapid emergency call-out</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>Full IQ/OQ/PQ documentation and audit readiness</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

          </div>

        </div>

        {/* Bottom Feature Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 pt-8 border-t border-border">
          {highlights.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.title} data-index={i} className="flex items-start gap-4 p-4 rounded-lg bg-secondary/50 border border-border/60 transition-[color,background-color,border-color] duration-200 hover:bg-secondary">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
                  <IconComponent className="size-5" />
                </div>
                <div className="space-y-1 text-left">
                  <h3 className="font-semibold text-sm sm:text-base text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

      </div>
    </section>;
}